import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../hooks/useOrganization';
import { useDashboardMetrics } from '../../hooks/useDashboardMetrics';
import MetricCard from '../../components/common/MetricCard';
import Alert from '../../components/common/Alert';
import {
  ResponsiveContainer,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Area
} from 'recharts';
import {
  Mail,
  Users,
  CheckCircle,
  Activity,
  Building
} from 'lucide-react';

// Timeline chart component that expects a timelineData array
const EmailTimelineChart = ({ timelineData }) => {
  const processedData = React.useMemo(() => {
    if (!Array.isArray(timelineData)) return [];
    return timelineData
      .map((item) => ({
        date: item.date,
        total: Number(item.total || 0),
        verified: Number(item.verified || 0)
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [timelineData]);

  if (processedData.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-medium text-midnight mb-4 font-heading">Email Activity</h3>
        <div className="h-64 flex items-center justify-center">
          <p className="text-ebony">No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-lg font-medium text-midnight mb-4 font-heading">Email Activity</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={processedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(value) =>
                new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              }
            />
            <YAxis />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-white p-3 border rounded-lg shadow-md">
                    <p className="font-medium">{new Date(label).toLocaleDateString()}</p>
                    <p className="text-sm text-violet">Total Emails: {payload[0]?.value}</p>
                    <p className="text-sm text-spring-green">Verified: {payload[1]?.value}</p>
                  </div>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="total"
              name="Total Emails"
              stroke="#5E3258"
              fill="#5E3258"
              fillOpacity={0.2}
            />
            <Area
              type="monotone"
              dataKey="verified"
              name="Verified"
              stroke="#48BB78"
              fill="#48BB78"
              fillOpacity={0.2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Displays the current user's email activity
const UserDashboard = ({ metrics, isLoading }) => (
  <div className="space-y-6">
    <h2 className="text-lg font-medium text-midnight font-heading">Your Activity</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <MetricCard
        title="Total Emails"
        icon={<Mail className="h-5 w-5 text-sky-blue" />}
        isLoading={isLoading}
      >
        <div className="text-3xl font-bold text-violet">
          {metrics?.totalEmails?.toLocaleString() || '0'}
        </div>
        <p className="text-sm text-ebony mt-2">All time sent emails</p>
      </MetricCard>
      <MetricCard
        title="Daily Emails"
        icon={<Activity className="h-5 w-5 text-sky-blue" />}
        isLoading={isLoading}
      >
        <div className="text-3xl font-bold text-violet">
          {metrics?.dailyEmails?.toLocaleString() || '0'}
        </div>
        <p className="text-sm text-ebony mt-2">Last 24 hours</p>
      </MetricCard>
      <MetricCard
        title="Verification Rate"
        icon={<CheckCircle className="h-5 w-5 text-spring-green" />}
        isLoading={isLoading}
      >
        <div className="text-3xl font-bold text-spring-green">
          {`${metrics?.verificationRate || '0'}%`}
        </div>
        <p className="text-sm text-ebony mt-2">Average success rate</p>
      </MetricCard>
    </div>
    {metrics?.timelineData && metrics.timelineData.length > 0 && (
      <EmailTimelineChart timelineData={metrics.timelineData} />
    )}
  </div>
);

// Displays organization-level aggregated metrics
const AdminDashboard = ({ orgStats, isLoading }) => (
  <div className="space-y-6">
    <h2 className="text-lg font-medium text-midnight font-heading">Organization Overview</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <MetricCard
        title="Total Organization Emails"
        icon={<Mail className="h-5 w-5 text-violet" />}
        isLoading={isLoading}
      >
        <div className="text-3xl font-bold text-violet">
          {orgStats?.totalEmails?.toLocaleString() || '0'}
        </div>
        <p className="text-sm text-ebony mt-2">Organization-wide total</p>
      </MetricCard>
      <MetricCard
        title="Active Users"
        icon={<Users className="h-5 w-5 text-sky-blue" />}
        isLoading={isLoading}
      >
        <div className="text-3xl font-bold text-violet">
          {orgStats?.activeUsers?.toLocaleString() || '0'}
        </div>
        <p className="text-sm text-ebony mt-2">Active members</p>
      </MetricCard>
      <MetricCard
        title="Organization Success Rate"
        icon={<CheckCircle className="h-5 w-5 text-spring-green" />}
        isLoading={isLoading}
      >
        <div className="text-3xl font-bold text-spring-green">
          {`${orgStats?.verificationRate || '0'}%`}
        </div>
        <p className="text-sm text-ebony mt-2">Organization verification rate</p>
      </MetricCard>
    </div>
  </div>
);

// Main Dashboard component
export default function Dashboard() {
  const { user } = useAuth();
  const { organization, canViewOrgData } = useOrganization();
  const {
    emailMetrics,  // user-specific stats
    orgStats,      // organization-level stats
    isLoading,
    error,
    loadingEmailMetrics,
    loadingOrgStats
  } = useDashboardMetrics();

  if (error) {
    console.error('Dashboard Error:', error);
    return (
      <Alert type="error" title="Error loading dashboard">
        {error.message || 'Unable to load metrics. Please try again later.'}
      </Alert>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-midnight font-heading">Dashboard</h1>
        {canViewOrgData && organization?.name && (
          <div className="flex items-center gap-2">
            <Building className="h-5 w-5 text-ebony" />
            <span className="text-sm text-ebony">{organization.name}</span>
          </div>
        )}
      </div>

      <UserDashboard metrics={emailMetrics} isLoading={loadingEmailMetrics} />

      {canViewOrgData && (
        <AdminDashboard orgStats={orgStats} isLoading={loadingOrgStats} />
      )}
    </div>
  );
}